import { useState } from 'react';
import { Check } from 'lucide-react';
import { forgotPassword } from '../../../lib/funnelAuthClient';
import { fieldInputVariants, primaryButtonVariants, Spinner, ErrorBanner } from '../authFormPrimitives';

// Ported from roammyway_funnel's ForgotPasswordContent.tsx -- calls the
// funnel's real /api/auth/forgot-password directly (already plain JSON,
// no next-auth involved).
export default function ForgotPasswordStep({ initialEmail, onBack }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    try {
      const { ok, data } = await forgotPassword(email);
      if (!ok) {
        setError(data.error || 'An error occurred while requesting password reset');
        return;
      }
      setSuccess('If an account exists for that email, a password reset link has been sent.');
    } catch {
      setError('Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-left font-brand text-[26px] font-bold leading-[1.2] tracking-[-0.3px] text-brand-blue lg:text-center lg:text-[28px] lg:tracking-[-0.4px]">
        Reset your password
      </h1>
      <p className="mt-1 text-left font-brand text-sm text-brand-text-secondary lg:text-center">
        Enter the email on your account and we'll send you a link to reset your password.
      </p>

      {error && (
        <div className="mt-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {success ? (
        <div className="mt-6 py-2 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-success/10">
              <Check className="h-7 w-7 text-brand-success" aria-hidden="true" strokeWidth={2.5} />
            </div>
          </div>
          <p className="font-brand text-sm text-brand-text-secondary">{success}</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-4 space-y-4" noValidate>
          <div className="space-y-1.5">
            <label htmlFor="romaya-forgot-password-email" className="font-brand text-[13px] font-semibold text-brand-blue">
              Email address
            </label>
            <input
              id="romaya-forgot-password-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              disabled={loading}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className={fieldInputVariants({ filled: Boolean(email) })}
            />
          </div>

          <button type="submit" disabled={loading} className={primaryButtonVariants({ tone: 'idle' })}>
            {loading ? (
              <span className="flex items-center justify-center">
                <Spinner className="-ml-1 mr-2 text-white" />
                Sending...
              </span>
            ) : (
              'Send reset link'
            )}
          </button>
        </form>
      )}

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onBack}
          className="font-brand text-sm font-semibold text-brand-teal-strong transition-colors hover:text-brand-blue focus:outline-none focus-visible:underline"
        >
          Back to sign in
        </button>
      </div>
    </>
  );
}
